import { HStack, Text, VStack } from "@chakra-ui/react"
import { FaSpinner } from "react-icons/fa6"
import {
  AssetMonitoringActivateDocument,
  AssetMonitoringDeactivateDocument,
  MonitoringFeatureInput,
} from "@hydrologiq/gql/graphql"
import { Asset } from "@hydrologiq/gql-experience/graphql"
import Figure from "../generic/Figure"
import FigureContent from "../generic/FigureContent"
import { isCompactFigure } from "@hydrologiq/shared/figures/generic/figureVariant"
import { useState } from "react"
import MonitoringActivationWorkflow, {
  useMonitoringActivationWorkflow,
} from "@hydrologiq/shared/chakra/MonitoringActivationWorkflow"
import { useMutation } from "@apollo/client"
import AssetMonitoringActivationPopover from "./AssetMonitoringActivationPopover"
import { useRmsStatusPolling } from "../../hooks/useRmsStatusPolling"
import { formatHHMMSS } from "@hydrologiq/common/helpers/date"
import DataConnectionIndicator from "../../data-connections/DataConnectionIndicator"

export interface MonitoringStatusData {
  status: string
  assignmentId?: string | null
  installationId?: string | null
  assignmentStart?: string | null
  monitoringInstanceId?: string | null
  monitoringInstanceName?: string | null
  monitorId?: string | null
  monitorName?: string | null
  monitorKind?: string | null
}

export interface MonitoringStatusProps {
  context?: Asset
  monitoringStatusData?: MonitoringStatusData | null
  disabled?: boolean
  heading?: string
  onRefresh?: () => void
  onChange?: () => void
}

const MonitoringStatus = ({
  context,
  monitoringStatusData,
  disabled = false,
  heading = "Data Connections",
  onRefresh,
  onChange,
}: MonitoringStatusProps) => {
  // Use the reusable monitoring workflow hook
  const workflow = useMonitoringActivationWorkflow()
  const [showActivationPopover, setShowActivationPopover] = useState(false)
  const [selectedMonitoringInstanceId, setSelectedMonitoringInstanceId] = useState<string | null>(null)

  // GraphQL mutations
  const [activateMonitoring, { loading: activatingLoading }] = useMutation(AssetMonitoringActivateDocument)
  const [deactivateMonitoring, { loading: deactivatingLoading }] = useMutation(AssetMonitoringDeactivateDocument)

  // Find active assignment if passed on context to determine site/system management
  const assignmentsAll = (context as any)?.assignments?.all as any[] | undefined
  const latestId = (context as any)?.assignments?.latest as string | undefined
  const activeAssignment = assignmentsAll?.find((a) => a.id === latestId) || (context as any)?.activeAssignment

  // A child asset's monitoring is managed through its parent, not directly.
  const isChildAsset = !!context?.parentId

  const isManagedBySystem = !!activeAssignment?.system || !!(context as any)?.systemId
  const isManagedBySite =
    !isManagedBySystem &&
    (!!activeAssignment?.installation_override ||
      !!activeAssignment?.installation ||
      !!monitoringStatusData?.installationId)
  const ownershipLevel = isManagedBySystem
    ? "system"
    : isManagedBySite
      ? "site"
      : isChildAsset
        ? "parent"
        : "asset"

  // Check if any child module has active monitoring status
  const hasActiveChild = Boolean(
    context?.children?.some((child) => child.monitoring_status?.toLowerCase() === "active")
  )

  // Derive display status: supports connection state reporting from System or Asset
  const rawStatus = monitoringStatusData?.status?.toLowerCase()
  const isActive = rawStatus === "active" || hasActiveChild

  // A connection exists if monitoring is active or if a monitor/instance/assignment is assigned
  const hasConnection = Boolean(
    isActive ||
    monitoringStatusData?.monitoringInstanceId ||
    monitoringStatusData?.monitorId ||
    monitoringStatusData?.monitorName ||
    monitoringStatusData?.assignmentId ||
    monitoringStatusData?.assignmentStart
  )

  let statusText = "No connections"
  if (hasConnection) {
    statusText = isActive ? "Active" : "Inactive"
  } else if (isManagedBySystem) {
    statusText = "Managed by system"
  } else if (isManagedBySite) {
    statusText = "Managed by site"
  } else if (isChildAsset) {
    statusText = "Managed by parent"
  }

  // Get monitor name and instance name from headlines data when a connection or monitor exists
  const monitorName = hasConnection ? monitoringStatusData?.monitorName : null
  const instanceName = hasConnection ? monitoringStatusData?.monitoringInstanceName : null

  // Format subtext as "[monitor name] ([input name])"
  const subtext = monitorName ? `${monitorName} ${instanceName ? `(${instanceName})` : ""}` : undefined

  const isMutationLoading = activatingLoading || deactivatingLoading

  // RMS status polling for active RMS monitors
  const { rmsStatus, lastChecked } = useRmsStatusPolling({
    assetId: context?.id,
    installationId: monitoringStatusData?.installationId ?? undefined,
    assignmentStatus: isActive ? "active" : "inactive",
    assignmentStart: monitoringStatusData?.assignmentStart ?? undefined,
    monitorKind: monitoringStatusData?.monitorKind ?? undefined,
    isMonitoringActive: isActive,
  })

  // Handle activation from popover (new instance)
  const handleActivateFromPopover = (
    monitoringInstanceId: string,
    features?: MonitoringFeatureInput,
    volume?: number | null,
    min_pressure?: number | null
  ) => {
    setSelectedMonitoringInstanceId(monitoringInstanceId)
    setSelectedFeatures(features)
    setVolume(volume ?? null)
    setMinPressure(min_pressure ?? null)
    setShowActivationPopover(false)
    workflow.openActivation()
  }

  // State for selected features during activation
  const [selectedFeatures, setSelectedFeatures] = useState<MonitoringFeatureInput | undefined>()

  // State for volume and pressure from popover
  const [volume, setVolume] = useState<number | null>(null)
  const [minPressure, setMinPressure] = useState<number | null>(null)

  // Workflow callbacks
  const handleWorkflowSuccess = () => {
    setSelectedMonitoringInstanceId(null)
    setSelectedFeatures(undefined)
    setVolume(null)
    setMinPressure(null)
    if (onRefresh) {
      onRefresh()
    }
  }

  const handleWorkflowError = (error: any) => {
    console.error("Monitoring workflow error:", error)
  }

  // Handle activation mutation
  const handleActivate = async (volumeParam?: number | null, min_pressureParam?: number | null) => {
    if (!context?.id) throw new Error("Missing context ID")

    const assignmentOrInstance =
      selectedMonitoringInstanceId ||
      monitoringStatusData?.assignmentId ||
      monitoringStatusData?.monitoringInstanceId ||
      ""

    const finalVolume = volume ?? volumeParam
    const finalMinPressure = minPressure ?? min_pressureParam

    const result = await activateMonitoring({
      variables: {
        id: context.id,
        assignment_or_instance: assignmentOrInstance,
        features: selectedFeatures,
        volume: finalVolume ?? undefined,
        min_pressure: finalMinPressure ?? undefined,
      },
    })

    if (result.errors && result.errors.length > 0) {
      const errorMessage = result.errors[0].message || "Activation failed"
      throw new Error(errorMessage)
    } else {
      if (onChange) onChange()
    }
  }

  // Handle deactivation mutation
  const handleDeactivate = async (volumeParam?: number | null, min_pressureParam?: number | null) => {
    if (!context?.id) throw new Error("Missing context ID")

    const assignmentOrInstance = monitoringStatusData?.assignmentId || monitoringStatusData?.monitoringInstanceId || ""

    const finalVolume = volume ?? volumeParam
    const finalMinPressure = minPressure ?? min_pressureParam

    const result = await deactivateMonitoring({
      variables: {
        id: context.id,
        assignment_or_instance: assignmentOrInstance,
        volume: finalVolume ?? undefined,
        min_pressure: finalMinPressure ?? undefined,
      },
    })

    if (result.errors && result.errors.length > 0) {
      const errorMessage = result.errors[0].message || "Deactivation failed"
      throw new Error(errorMessage)
    } else {
      if (onChange) onChange()
    }
  }

  return (
    <>
      <Figure
        heading={heading}
        headingExtra={
          context ? (
            <DataConnectionIndicator
              asset={context as any}
              hasConnection={hasConnection}
            />
          ) : undefined
        }
        loading={!context}
        isPlaceholder={!monitoringStatusData}
        compact={isCompactFigure("summary")}
        popover={
          subtext
            ? {
              title: heading,
              content: (
                <VStack width="100%" align="start" gap={1}>
                  <HStack>
                    <Text fontSize="sm" fontWeight="medium">
                      Monitor:
                    </Text>
                    <Text fontSize="sm">{monitorName || "Unknown"}</Text>
                  </HStack>
                  <HStack>
                    <Text fontSize="sm" fontWeight="medium">
                      Input:
                    </Text>
                    <Text fontSize="sm">{instanceName || "Unknown"}</Text>
                  </HStack>
                  {isActive ? (
                    <HStack>
                      <Text fontSize="sm" fontWeight="medium">
                        Last checked:
                      </Text>
                      <Text fontSize="sm">{lastChecked ? formatHHMMSS(lastChecked) : "Unknown"}</Text>
                    </HStack>
                  ) : null}
                </VStack>
              ),
            }
            : undefined
        }
      >
        <FigureContent.Root>
          <FigureContent.SubText link={rmsStatus ? (
            <HStack gap={1} alignItems="center">
              <Text fontSize="xs" color="gray.600" data-testid="rms-status">
                {rmsStatus}
              </Text>
              {rmsStatus === "Initiating" && (
                <FaSpinner
                  size="10"
                  style={{
                    animation: "spin 1s linear infinite",
                    color: "#718096",
                  }}
                />
              )}
            </HStack>
          ) : undefined}>{`current (${ownershipLevel})`}</FigureContent.SubText>
          <HStack gap={2} alignItems="center" justifyContent="space-between" width="100%">
            <HStack gap={2} alignItems="center" flex="1">
              <FigureContent.Value>{statusText}</FigureContent.Value>
            </HStack>
          </HStack>
        </FigureContent.Root>
      </Figure>

      {/* Asset Monitoring Activation Popover */}
      {context && (
        <AssetMonitoringActivationPopover
          open={showActivationPopover}
          onClose={() => setShowActivationPopover(false)}
          onActivate={handleActivateFromPopover}
          assetId={context.id}
          asset={context}
          loading={isMutationLoading}
        />
      )}

      {/* Reusable Monitoring Activation Workflow */}
      {context && monitoringStatusData && (
        <MonitoringActivationWorkflow
          open={workflow.isOpen}
          action={workflow.action ?? undefined}
          assetKind={context.kind ?? null}
          assetId={context.id ?? null}
          onClose={workflow.close}
          onSuccess={handleWorkflowSuccess}
          onError={handleWorkflowError}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          entityName="monitoring"
          defaultVolume={volume}
          defaultMinPressure={minPressure}
        />
      )}
    </>
  )
}

export default MonitoringStatus
